import React, { useEffect, useState } from "react";
import { TwitterPicker } from "react-color";
import LoadingSpinnerImage from "../Shared/LoadingSpinnerImage.js";
import * as FieldTypes from "../Shared/FieldTypes/FieldTypes.js";

const BoardView = ({ tasks, boards, users, onOpenTask, onConfigure }) => {
    const steps = [...(boards?.[0]?.Steps || [])].sort((a, b) => a.BoardStepOrder - b.BoardStepOrder);

    if (steps.length === 0) {
        return (
            <div style={{ marginTop: "50px" }}>
                <span>This is a fresh new board! Add your first step to start using it.</span>
                <br /><br />
                <span style={{ padding: "6px 15px", backgroundColor: "#cff4ff", borderRadius: "10px", cursor: "pointer" }} onClick={onConfigure}>Add a step</span>
            </div>
        );
    }

    return (
        <div className="thin-scrollbar board-view">
            {steps.map((step) => (
                <div key={step.BoardStepId} className="board-view-step">
                    <div style={{ backgroundColor: step.BoardStepColor, height: "5px" }}></div>
                    <div style={{ textAlign: "center" }}><h3>{step.BoardStepName}</h3></div>
                    <div className="thin-scrollbar" style={{ height: "100%", overflowY: "auto" }}>
                        {tasks.filter((task) => task.BoardStep == step.BoardStepId).map((task) => (
                            <div
                                key={task.Id}
                                onClick={() => onOpenTask(task.Id)}
                                style={{ cursor: "pointer", margin: "2px 2px 10px", padding: "5px", backgroundColor: "white", border: "1px solid #c0c0c0", borderRadius: "5px" }}
                            >
                                <div style={{ fontWeight: "bold", textAlign: "left" }}>{task.Name}</div>
                                <div style={{ display: "flex", alignItems: "center", marginTop: "8px" }}>
                                    <span style={{ marginRight: "5px" }}>Priority:</span>
                                    <FieldTypes.FieldType_TaskPriority value={task.Priority} />
                                </div>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ marginRight: "5px" }}>Due Date:</span>
                                    <FieldTypes.FieldType_TaskDueDate value={task.DueDate} />
                                </div>
                                <div style={{ display: "flex", marginTop: "8px" }}>
                                    <FieldTypes.FieldType_User value={task.AssignedTo} users={users} />
                                    {task.CompletionStatus == 1 && (
                                        <span style={{ marginLeft: "8px" }}><FieldTypes.FieldType_TaskCompletion value={task.CompletionStatus} /></span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

const BoardConfiguration = ({ boards, onSaved, onBack }) => {
    const [steps, setSteps] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [colorIndex, setColorIndex] = useState(null);
    const [newName, setNewName] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        setSteps(JSON.parse(JSON.stringify(boards?.[0]?.Steps || [])));
    }, [boards]);

    const sorted = [...steps].sort((a, b) => a.BoardStepOrder - b.BoardStepOrder);

    const rename = (id, name) => {
        setSteps(steps.map((step) => step.BoardStepId == id ? { ...step, BoardStepName: name } : step));
    };

    const reorder = (id, direction) => {
        const current = steps.find((step) => step.BoardStepId == id);
        if (!current)
            return;
        const nextOrder = direction === "up" ? current.BoardStepOrder - 1 : current.BoardStepOrder + 1;
        if (nextOrder < 1 || nextOrder > steps.length)
            return;
        setSteps(steps.map((step) => {
            if (step.BoardStepOrder == current.BoardStepOrder)
                return { ...step, BoardStepOrder: nextOrder };
            if (step.BoardStepOrder == nextOrder)
                return { ...step, BoardStepOrder: current.BoardStepOrder };
            return step;
        }));
    };

    const remove = (id) => {
        const removed = steps.find((step) => step.BoardStepId == id);
        setSteps(steps.filter((step) => step.BoardStepId != id).map((step) => (
            step.BoardStepOrder > removed.BoardStepOrder ? { ...step, BoardStepOrder: step.BoardStepOrder - 1 } : step
        )));
    };

    const add = () => {
        if (!newName || !boards?.[0])
            return;
        const tempId = steps.reduce((min, step) => Math.min(min, step.BoardStepId), 0) - 1;
        setSteps([...steps, {
            BoardId: boards[0].Id,
            BoardStepId: tempId,
            BoardStepName: newName,
            BoardStepColor: "#abb8c3",
            BoardStepOrder: steps.length + 1,
        }]);
        setNewName("");
    };

    const save = () => {
        if (!boards?.[0])
            return;
        const boardToSave = JSON.parse(JSON.stringify(boards[0]));
        boardToSave.Steps = sorted.map((step) => ({ ...step, BoardStepId: step.BoardStepId < 0 ? 0 : step.BoardStepId }));
        setIsSaving(true);
        fetch("/umbraco/api/TaskManagerApi/Board", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(boardToSave),
        })
            .then((response) => {
                if (!response.ok)
                    throw new Error("Network response was not ok");
                return response.json();
            })
            .then(() => onSaved())
            .catch(() => { })
            .finally(() => setIsSaving(false));
    };

    return (
        <div>
            <div style={{ textAlign: "center" }}>
                <h2>Board Configuration</h2>
                <div style={{ marginBottom: "15px" }} dangerouslySetInnerHTML={{ __html: window.umbracoMessage_BoardConfiguration || "" }} />
            </div>
            {sorted.map((item, index) => (
                <div key={item.BoardStepId} style={{ display: "flex", marginBottom: "3px", backgroundColor: "#fafafa" }}>
                    <div style={{ width: "10px", borderLeft: "7px solid " + item.BoardStepColor }}></div>
                    <div style={{ width: "200px", display: "flex", alignItems: "center", padding: "4px 0" }}>
                        {editingId !== item.BoardStepId ? (
                            <span className="editable-field-span ellipsis" onClick={() => setEditingId(item.BoardStepId)}>{item.BoardStepName}</span>
                        ) : (
                            <input autoFocus value={item.BoardStepName} onBlur={() => setEditingId(null)} onChange={(e) => rename(item.BoardStepId, e.target.value)} />
                        )}
                    </div>
                    <div style={{ width: "25px", display: "flex", alignItems: "center" }}>
                        {index > 0 ? <span onClick={() => reorder(item.BoardStepId, "up")}><i className="fas fa-arrow-up"></i></span> : <i style={{ color: "#c0c0c0" }} className="fas fa-arrow-up"></i>}
                    </div>
                    <div style={{ width: "25px", display: "flex", alignItems: "center" }}>
                        {index < sorted.length - 1 ? <span onClick={() => reorder(item.BoardStepId, "down")}><i className="fas fa-arrow-down"></i></span> : <i style={{ color: "#c0c0c0" }} className="fas fa-arrow-down"></i>}
                    </div>
                    <div style={{ width: "60px", display: "flex", alignItems: "center" }}>
                        <div style={{ padding: "5px", background: "#fff", boxShadow: "0 0 0 1px rgba(0,0,0,.1)", cursor: "pointer" }} onClick={() => setColorIndex(index)}>
                            <div style={{ width: "36px", height: "14px", background: item.BoardStepColor }} />
                        </div>
                        {colorIndex == index && (
                            <div style={{ position: "absolute", zIndex: 2 }}>
                                <div style={{ position: "fixed", inset: 0 }} onClick={() => setColorIndex(null)} />
                                <TwitterPicker color={item.BoardStepColor} onChange={(color) => setSteps(steps.map((step) => step.BoardStepId == item.BoardStepId ? { ...step, BoardStepColor: color.hex } : step))} />
                            </div>
                        )}
                    </div>
                    <div style={{ width: "25px", display: "flex", alignItems: "center" }}>
                        <span onClick={() => remove(item.BoardStepId)}><i className="far fa-trash-alt"></i></span>
                    </div>
                </div>
            ))}
            <div style={{ display: "flex", padding: "3px", border: "1px dashed #c0c0c0" }}>
                <input type="text" placeholder="Column name..." value={newName} onChange={(e) => setNewName(e.target.value)} />
                <button style={{ marginLeft: "10px" }} className="action-button save-button" onClick={add}>Add</button>
            </div>
            <div style={{ marginTop: "20px", textAlign: "center" }}>
                {isSaving ? <LoadingSpinnerImage /> : (
                    <>
                        <button className="action-button save-button" onClick={save}>Save Board Configuration</button>
                        <button className="action-button cancel-button" style={{ marginLeft: "10px" }} onClick={onBack}>Back to board</button>
                    </>
                )}
            </div>
        </div>
    );
};

export { BoardView, BoardConfiguration };
